import { useEffect, useMemo, useState } from "react";
import * as S from "./styles";
import { SVG } from "~/components/ui/SVG";
import { colors } from "~/constants/theme";
import { useTranslation } from "react-i18next";
import { ShipsToFilters } from "~/constants/filters";
import { SortKey } from "~/constants/product-sorting";
import FlexContainer from "~/components/ui/FlexContainer";
import { faTimes } from "@fortawesome/pro-solid-svg-icons";

enum ParamTypes {
  FAST_SHIPPING = "fastShipping",
  SHIP_TO = "shipsTo",
  BEST_SELLING = "bestSelling",
  SORT = "sort",
}

type FilteredBadgeProps = {
  key: ParamTypes;
  value: string | undefined;
  label: string | boolean | undefined;
};

type Props = {
  fastShipping?: boolean;
  shipsTo?: string;
  bestSelling?: boolean;
  sortingParams?: string;
  clearAll: () => void;
  clear: (key: ParamTypes) => void;
};

const FilteredLayout = ({
  fastShipping,
  shipsTo,
  bestSelling,
  sortingParams,
  clear,
  clearAll,
}: Props) => {
  const { t } = useTranslation();
  const [sort, setSort] = useState<string | undefined>();
  const [country, setCountry] = useState<string | undefined>();

  const sortedLabelsByKey: Record<string, string> = {
    [SortKey.ASC]: "dropshipping.sort_price_asc",
    [SortKey.DESC]: "dropshipping.sort_price_desc",
    [SortKey.ORDERS]: "dropshipping.sort_orders_desc",
  };

  useEffect(() => {
    if (shipsTo) {
      const filteredCountry = ShipsToFilters.find(
        (data) => data.value === shipsTo
      );
      setCountry(filteredCountry?.labelKey);
    }
    if (sortingParams) {
      const sortLabel = sortedLabelsByKey[sortingParams];
      setSort(sortLabel);
    }
  }, [shipsTo, sortingParams]);

  const filters: Array<FilteredBadgeProps> = useMemo(
    () =>
      [
        {
          key: ParamTypes.FAST_SHIPPING,
          label: fastShipping,
          value: "dropshipping.fast_shipping",
        },
        { key: ParamTypes.SHIP_TO, label: country, value: country },
        {
          key: ParamTypes.BEST_SELLING,
          label: bestSelling,
          value: "dropshipping.best_selling",
        },
        { key: ParamTypes.SORT, label: sortingParams, value: sort },
      ].filter((filter) => filter.label),
    [sort, sortingParams, fastShipping, country, bestSelling]
  );

  return (
    <FlexContainer
      flexDirection="row"
      flexWrap="wrap"
      padding="15px 0"
      justifyContent="flex-start"
      gap="10px"
      width="100%">
      {filters.map(
        ({ key, label, value }) =>
          label &&
          !!value && (
            <S.FilterBadge key={value} onClick={() => clear(key)}>
              <S.BadgeText>{t(value)}</S.BadgeText>
              <S.SVGContainer>
                <SVG icon={faTimes} size="xs" color={colors.textSecondary} />
              </S.SVGContainer>
            </S.FilterBadge>
          )
      )}

      <S.ClearButton outline onClick={clearAll}>
        {t("Clear All")}
        <S.SVGContainer>
          <SVG icon={faTimes} size="xs" color={colors.textSecondary} />
        </S.SVGContainer>
      </S.ClearButton>
    </FlexContainer>
  );
};

export default FilteredLayout;
